// frontend/src/components/Footer.tsx
import { Link } from 'react-router-dom';
import { Sparkles } from 'lucide-react';
import { useI18n } from '../contexts/I18nContext';

interface FooterProps {
  variant?: 'full' | 'compact' | 'minimal';
  showCreatorCTA?: boolean;
}

export function Footer({ variant = 'full', showCreatorCTA = false }: FooterProps) {
  const { tt } = useI18n();

  if (variant === 'minimal') {
    return (
      <footer className="border-t border-theme-border-muted bg-theme-bg/80">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-[10px] sm:text-xs text-theme-text-muted">
              © 2026 <a href="https://chapterpal.com" target="_blank" rel="noopener noreferrer" className="hover:text-theme-text transition-colors">ChapterPal Inc</a>. • {tt('Powered by Llambada')}
            </p>
            {showCreatorCTA && (
              <Link
                to="/creators"
                className="inline-flex items-center gap-1.5 text-[10px] sm:text-xs text-accent-500 hover:text-accent-400 font-medium transition-colors"
              >
                <Sparkles className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
                {tt('Build your own mini-app')}
              </Link>
            )}
          </div>
        </div>
      </footer>
    );
  }

  if (variant === 'compact') {
    return (
      <footer className="border-t border-theme-border-muted bg-theme-bg/80">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-sm text-theme-text-muted">
              © 2026 <a href="https://chapterpal.com" target="_blank" rel="noopener noreferrer" className="hover:text-theme-text transition-colors">ChapterPal Inc</a>.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4 text-sm text-theme-text-muted">
              <Link to="/terms" className="hover:text-theme-text transition-colors">{tt('Terms')}</Link>
              <Link to="/privacy" className="hover:text-theme-text transition-colors">{tt('Privacy')}</Link>
              <Link to="/contact" className="hover:text-theme-text transition-colors">{tt('Contact')}</Link>
            </div>
          </div>
        </div>
      </footer>
    );
  }

  // Full variant (default)
  return (
    <footer className="border-t border-theme-border-muted bg-theme-bg/80">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <p className="text-sm text-theme-text-muted">
            © 2026 <a href="https://chapterpal.com" target="_blank" rel="noopener noreferrer" className="hover:text-theme-text transition-colors">ChapterPal Inc</a>.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-theme-text-muted">
            <Link to="/about" className="hover:text-theme-text transition-colors">{tt('About')}</Link>
            <Link to="/docs" className="hover:text-theme-text transition-colors">{tt('Documentation')}</Link>
            <Link to="/creators" className="hover:text-theme-text transition-colors">{tt('Creators')}</Link>
            <Link to="/pricing" className="hover:text-theme-text transition-colors">{tt('Pricing')}</Link>
            <Link to="/contact" className="hover:text-theme-text transition-colors">{tt('Contact')}</Link>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-theme-text-muted">
            <Link to="/terms" className="hover:text-theme-text transition-colors">{tt('Terms')}</Link>
            <Link to="/privacy" className="hover:text-theme-text transition-colors">{tt('Privacy')}</Link>
            <Link to="/copyright" className="hover:text-theme-text transition-colors">{tt('Copyright')}</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
